import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { LocalStorage } from 'ngx-webstorage';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defaultIfEmpty,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
  timeout,
  timer,
} from 'rxjs';
import { AccountAttribute } from 'app/enums/account-attribute.enum';
import { LoginResult } from 'app/enums/login-result.enum';
import { Role } from 'app/enums/role.enum';
import { filterAsync } from 'app/helpers/operators/filter-async.operator';
import { WINDOW } from 'app/helpers/window.helper';
import { LoginExResponse, LoginExResponseType } from 'app/interfaces/auth.interface';
import { LoggedInUser, toLoggedInUser } from 'app/interfaces/ds-cache.interface';
import { GlobalTwoFactorConfig } from 'app/interfaces/two-factor-config.interface';
import { PendingTwoFactorService } from 'app/modules/auth/pending-two-factor.service';
import { asLoginExResponse } from 'app/modules/auth/typed-login-response';
import { ApiService } from 'app/modules/websocket/api.service';
import { TYPED_API_CLIENT } from 'app/modules/websocket/typed-api/typed-api-client.token';
import { ErrorHandlerService } from 'app/services/errors/error-handler.service';
import { TokenLastUsedService } from 'app/services/token-last-used.service';
import { WebSocketStatusService } from 'app/services/websocket-status.service';
import { AppState } from 'app/store';
import { adminUiInitialized } from 'app/store/admin-panel/admin.actions';
import { minSessionLifetime } from 'app/store/preferences/default-preferences.constant';

/** How long the typed logout waits for middleware's acknowledgement before giving up on it. */
const logoutAckTimeoutMs = 10_000;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private api = inject(ApiService);
  private client$ = inject(TYPED_API_CLIENT);
  private tokenLastUsedService = inject(TokenLastUsedService);
  private wsStatus = inject(WebSocketStatusService);
  private errorHandler = inject(ErrorHandlerService);
  private pendingTwoFactor = inject(PendingTwoFactorService);
  private window = inject<Window>(WINDOW);
  private destroyRef = inject(DestroyRef);

  @LocalStorage() private token: string | undefined | null;
  protected loggedInUser$ = new BehaviorSubject<LoggedInUser | null>(null);

  // Store pending authentication data before session initialization
  private pendingAuthData: {
    userInfo: LoggedInUser;
  } | null = null;

  // Flag to prevent premature adminUiInitialized dispatch
  private sessionInitialized = false;

  /**
   * A ceiling on the renewal interval. Middleware cuts the token's TTL from the configured
   * lifetime, but caps it at the authenticator assurance level's maximum session age — thirteen
   * minutes at the strictest level — and `auth.generate_token` hands back only the token, never
   * the TTL it granted. Renewing at least this often keeps the stored token live under any cap
   * the appliance applies, at the cost of one call every five minutes on a long session.
   */
  private readonly maxRenewalIntervalSeconds = 300;

  private latestTokenGenerated$ = new ReplaySubject<string | null>(1);
  get authToken$(): Observable<string> {
    return this.latestTokenGenerated$.asObservable().pipe(filter((token): token is string => !!token));
  }

  get hasAuthToken(): boolean {
    return Boolean(this.token) && this.token !== 'null';
  }

  readonly user$ = this.loggedInUser$.asObservable();

  readonly isLocalUser$: Observable<boolean> = this.user$.pipe(
    filter(Boolean),
    map((user) => user.account_attributes.includes(AccountAttribute.Local)),
  );

  /**
   * Whether the current user is allowed to open a Web Shell. Every shell
   * (system, VM serial, container console) connects through the same
   * `/websocket/shell/` endpoint, which is gated by the `web_shell` privilege.
   *
   * Only emits for a resolved (non-null) user. `take(1)`-gated consumers (e.g.
   * the terminal access check) rely on this: shell routes sit behind AuthGuard,
   * so a user is always resolved by the time they activate. Filtering out the
   * transient nulls (initial seed, refreshUser, reconnect) means we wait for the
   * re-resolved user instead of snapshotting a premature `false` and denying
   * access permanently.
   */
  readonly hasWebShellAccess$: Observable<boolean> = this.user$.pipe(
    filter(Boolean),
    map((user) => Boolean(user.privilege?.web_shell)),
  );

  private readonly hasPasswordChangedSinceLastLogin$ = new BehaviorSubject(false);
  readonly isPasswordChangeRequired$: Observable<boolean> = combineLatest([
    this.user$.pipe(
      filter(Boolean),
      map((user) => user.account_attributes.includes(AccountAttribute.PasswordChangeRequired)),
    ),
    this.hasPasswordChangedSinceLastLogin$,
  ]).pipe(
    map(([changeRequired, changedSinceLastLogin]) => changeRequired && !changedSinceLastLogin),
  );

  /**
   * Special case that only matches root and admin users.
   */
  readonly isSysAdmin$ = this.user$.pipe(
    filter(Boolean),
    map((user) => user.account_attributes.includes(AccountAttribute.SysAdmin)),
  );

  readonly userTwoFactorConfig$ = this.user$.pipe(
    filter(Boolean),
    map((user) => user.two_factor_config),
  );

  private readonly cachedGlobalTwoFactorConfig$ = new BehaviorSubject<GlobalTwoFactorConfig | null>(null);

  constructor() {
    this.setupAuthenticationUpdate();
    this.setupWsConnectionUpdate();
    this.setupTokenUpdate();
    this.setupTokenRenewal();
  }

  getGlobalTwoFactorConfig(): Observable<GlobalTwoFactorConfig> {
    return this.cachedGlobalTwoFactorConfig$.pipe(
      switchMap((cachedConfig) => {
        if (cachedConfig) {
          return of(cachedConfig);
        }

        return this.wsStatus.isAuthenticated$.pipe(
          take(1),
          filter(Boolean),
          switchMap(() => this.api.call('auth.twofactor.config').pipe(
            tap((config) => this.cachedGlobalTwoFactorConfig$.next(config)),
          )),
        );
      }),
    );
  }

  globalTwoFactorConfigUpdated(): void {
    this.cachedGlobalTwoFactorConfig$.next(null);
  }

  /**
   * This method exists so removing authToken is deliberate instead of allowing
   * use of the lastGeneratedToken$ and setting token to null/undefined by mistake
   */
  clearAuthToken(): void {
    this.window.sessionStorage.removeItem('loginBannerDismissed');
    this.tokenLastUsedService.clearTokenLastUsed();
    this.latestTokenGenerated$.next(null);
    this.latestTokenGenerated$.complete();
    this.latestTokenGenerated$ = new ReplaySubject<string>(1);
    this.setupTokenUpdate();
  }

  /**
   * Signs in on the typed client, which is where the app's session lives.
   *
   * The authenticator speaks `auth.login_ex` and owns the reconnect-token
   * chain, so a password login mints the token the next reconnect spends. It
   * reports a refused credential by throwing rather than by answering, which
   * `asLoginExResponse` puts back as the `AUTH_ERR` the UI already handles.
   */
  login(
    username: string,
    password: string,
    otp: string | null = null,
  ): Observable<{ loginResult: LoginResult; loginResponse: LoginExResponse }> {
    const loginCall$ = this.client$.pipe(
      // `loginWithUserPass` keeps the plaintext password on the authenticator and
      // replays it on every reconnect, and offers no way to decline. That is new
      // for this app, not just for the library: until the session moved here the
      // UI never held the password past the login frame, only the single-use
      // `reconnect_token` below. A password-backed session now carries the
      // credential in tab memory for its whole life. See the known-gaps list in
      // `docs/devs/typed-api-client.md`.
      switchMap((client) => (otp
        ? client.authenticator.loginWithOtp(otp)
        : client.authenticator.loginWithUserPass(username, password))),
      asLoginExResponse(),
    );

    return loginCall$.pipe(
      tap((result) => {
        // A successful OTP login validated a code against the account's current secret,
        // which is exactly what the pending marker is waiting for. Without clearing it
        // here the setup dialog reopens on the next guard run, offering only another code
        // or a Cancel Setup that deletes a secret the user demonstrably holds.
        if (otp && result.response_type === LoginExResponseType.Success && result.user_info?.pw_name) {
          this.pendingTwoFactor.clear(result.user_info.pw_name);
        }
      }),
      switchMap((result) => this.processLoginResult(result).pipe(
        map((loginResult) => ({
          loginResponse: result,
          loginResult,
        })),
      )),
    );
  }

  isTwoFactorSetupRequired(): Observable<boolean> {
    return this.wsStatus.isAuthenticated$.pipe(
      take(1),
      filter(Boolean),
      switchMap(() => this.getGlobalTwoFactorConfig().pipe(
        switchMap((globalConfig) => {
          if (!globalConfig.enabled) {
            return of(false);
          }

          // A secret that exists but has not been confirmed still needs the prompt: the
          // user may have reloaded mid-setup, and `secret_configured` alone would let
          // them past the dialog with an armed secret their app never received.
          return this.user$.pipe(
            filter(Boolean),
            map((user) => {
              return !user.two_factor_config.secret_configured
                || !!this.pendingTwoFactor.get(user.pw_name);
            }),
          );
        }),
      )),
      defaultIfEmpty(false),
    );
  }

  setQueryToken(token: string | null): void {
    const isSecure = this.window.location.protocol === 'https:' || !environment.production;
    if (!token || !isSecure) {
      return;
    }

    this.token = token;
  }

  /**
   * Spends the stored reconnect token on the typed client.
   *
   * Tokens are single-use, so every login mints the next one; `processLoginResult`
   * stores it. A token session is the one the client will not restore by itself
   * after a reconnect — by design, since the token it held has been spent — which
   * is why the sign-in page comes back through here on every reconnect.
   */
  loginWithToken(): Observable<LoginResult> {
    const token = this.token;
    if (!token) {
      return of(LoginResult.NoToken);
    }

    performance.mark('Login Start');
    return this.client$.pipe(
      switchMap((client) => client.authenticator.loginWithToken(token)),
      asLoginExResponse(),
      switchMap((loginResult) => this.processLoginResult(loginResult)),
      catchError((error: unknown) => {
        this.errorHandler.showErrorModal(error);
        return of(LoginResult.NoAccess);
      }),
    );
  }

  /**
   * Checks whether user has any of the supplied roles.
   * Does not ensure that user was loaded.
   *
   * Use mockAuth if you need to set user role in tests.
   */
  hasRole(roles: Role[] | Role): Observable<boolean> {
    return this.user$.pipe(
      filter(Boolean),
      map((user) => {
        const currentRoles = user?.privilege?.roles?.$set || [];
        const neededRoles = Array.isArray(roles) ? roles : [roles];

        if (!neededRoles?.length || !currentRoles.length) {
          return false;
        }

        return neededRoles.some((role) => currentRoles.includes(role));
      }),
    );
  }

  /**
   * Ends the session with `auth.logout`.
   *
   * The stored token goes first, before the call. `authenticator.logout()`
   * drops `authenticated$` at the call rather than when middleware answers, and
   * that walks the app to the sign-in page synchronously — which auto-logs-in
   * from the stored token. Clearing first leaves it nothing to log in with.
   *
   * Emits as soon as the frame is written rather than when it is acknowledged.
   * Every caller navigates to /signin on that emission, and the acknowledgement
   * may never come: a socket on its way down is the case this is written for.
   * The session is over locally either way — `endLocalSession` has already run
   * — so waiting would only decide how long the user stares at a menu.
   */
  logout(): Observable<void> {
    return this.client$.pipe(
      take(1),
      map((client): undefined => {
        this.endLocalSession();

        // Calling this is what ends the session: the authenticator clears its
        // credentials, drops `authenticated$` and writes the frame before it
        // returns. What it returns only carries middleware's acknowledgement,
        // so it is subscribed for its errors alone.
        client.authenticator.logout().pipe(
          // Bounded rather than left open for the life of the service.
          timeout(logoutAckTimeoutMs),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe({
          error: (error: unknown) => console.warn('Session logout was not acknowledged', error),
        });

        return undefined;
      }),
    );
  }

  /** Drops everything this tab knows about the session it was signed in on. */
  private endLocalSession(): void {
    this.clearAuthToken();
    this.hasPasswordChangedSinceLastLogin$.next(false);
    // No subscriptions to clear: the client drops every event subscription when the session ends
    // and sends `core.subscribe` again for the ones still held once the next login lands.
    this.wsStatus.setLoginStatus(false);
    this.sessionInitialized = false;
    this.pendingAuthData = null;
    this.loggedInUser$.next(null); // Clear user data on logout
    this.cachedGlobalTwoFactorConfig$.next(null); // Clear cached 2FA config
  }

  requiredPasswordChanged(): void {
    this.hasPasswordChangedSinceLastLogin$.next(true);
  }

  isFullAdmin(): Observable<boolean> {
    return this.hasRole([Role.FullAdmin]).pipe(take(1));
  }

  refreshUser(): Observable<undefined> {
    this.loggedInUser$.next(null);
    return this.getLoggedInUserInformation().pipe(
      map((): undefined => undefined),
    );
  }

  getOneTimeToken(): Observable<string> {
    return this.api.call('auth.generate_token', [300, {}, true, true]);
  }

  /**
   * Completes the login process by initializing the session.
   * This should only be called after all pre-flight checks (like failover) have passed.
   */
  initializeSession(): Observable<LoginResult> {
    if (!this.pendingAuthData) {
      return of(LoginResult.NoToken);
    }

    const { userInfo } = this.pendingAuthData;

    // Now safe to set the user and initialize the app
    this.loggedInUser$.next(userInfo);
    this.wsStatus.setLoginStatus(true);
    this.window.sessionStorage.setItem('loginBannerDismissed', 'true');

    // Mark session as initialized and dispatch adminUiInitialized
    this.sessionInitialized = true;
    this.store$.dispatch(adminUiInitialized());

    // Clear pending data
    this.pendingAuthData = null;

    return of(LoginResult.Success);
  }

  protected processLoginResult(loginResult: LoginExResponse): Observable<LoginResult> {
    return of(loginResult).pipe(
      switchMap((result) => {
        if (result.response_type === LoginExResponseType.Success) {
          if (!result.user_info?.privilege?.webui_access) {
            // Don't set login status here - wait for session initialization
            return of(LoginResult.NoAccess);
          }

          // Store authentication data but don't initialize session yet
          this.pendingAuthData = {
            userInfo: result.user_info,
          };

          // Store reconnect token from the login response
          if (result.reconnect_token) {
            this.latestTokenGenerated$.next(result.reconnect_token);
          }

          return of(LoginResult.Success);
        }

        // Don't set login status for error cases - it should remain false
        // Clean up any pending auth data on error
        this.pendingAuthData = null;

        if (result.response_type === LoginExResponseType.OtpRequired) {
          return of(LoginResult.NoOtp);
        }

        if (result.response_type === LoginExResponseType.Redirect) {
          return of(LoginResult.Redirect);
        }

        if (result.response_type === LoginExResponseType.Denied) {
          return of(LoginResult.Denied);
        }

        return of(LoginResult.IncorrectDetails);
      }),
    );
  }

  private getLoggedInUserInformation(): Observable<LoggedInUser> {
    return this.api.call('auth.me').pipe(
      map(toLoggedInUser),
      tap((loggedInUser) => {
        this.loggedInUser$.next(loggedInUser);
      }),
    );
  }

  protected setupAuthenticationUpdate(): void {
    this.wsStatus.isAuthenticated$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (isAuthenticated) => {
        if (isAuthenticated && this.sessionInitialized) {
          this.store$.dispatch(adminUiInitialized());
        } else if (!isAuthenticated) {
          this.cachedGlobalTwoFactorConfig$.next(null);
        }
      },
    });
  }

  /**
   * Drops the signed-in state when the app loses what it needs: the session it
   * is signed in on, or the connection that carries every call.
   *
   * Both end the same way — back to the sign-in page, which logs straight in
   * again when the stored token is still good.
   */
  protected setupWsConnectionUpdate(): void {
    merge(
      this.wsStatus.isConnected$,
      this.wsStatus.isSessionEstablished$,
    ).pipe(
      filter((isUp) => !isUp),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.wsStatus.setLoginStatus(false);
      this.loggedInUser$.next(null);
      // Reset session initialized flag when connection is lost
      this.sessionInitialized = false;
    });
  }

  protected setupTokenUpdate(): void {
    this.latestTokenGenerated$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((token) => {
      this.token = token;
    });
  }

  /**
   * The token minted at login carries an absolute TTL: it dies that many seconds after it was
   * issued, however busy the session was in between. The Session Timeout preference it is cut
   * from is an *inactivity* timeout, which `SessionTimeoutService` enforces on its own clock —
   * and nothing has kept the two in step since the periodic regeneration was dropped in favour
   * of the single token `auth.login_ex` hands back. So refreshing a page the user has been
   * working in all along lands on the sign-in page with "Session expired" as soon as that
   * deadline passes: five minutes in on a default install, twenty on a session configured for
   * twenty.
   *
   * Minting a replacement on a timer keeps a usable token in storage for as long as the session
   * is entitled to live, and re-minting when the preference changes lets a new Session Timeout
   * govern re-authentication without signing out first.
   */
  private setupTokenRenewal(): void {
    this.tokenLastUsedService.lifetime$.pipe(
      distinctUntilChanged(),
      switchMap((lifetime) => timer(0, this.getTokenRenewalInterval(lifetime)).pipe(map(() => lifetime))),
      filterAsync(() => this.wsStatus.isAuthenticated$),
      // A session middleware declined to hand a reconnect token to (an API key, a one-time
      // password) holds none, and must not be given one here. `auth.generate_token` refuses
      // those sessions as well, so the decision stays with middleware either way.
      filter(() => this.hasAuthToken),
      switchMap((lifetime) => this.api.call('auth.generate_token', [lifetime, {}, true, true]).pipe(
        catchError((error: unknown) => {
          console.error('Failed to renew the authentication token', error);
          return EMPTY;
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((token) => {
      // The gates above ran before the call went out. A sign-out in the meantime has already
      // swapped `latestTokenGenerated$`, so publishing here would put a live credential back in
      // storage after the user asked to leave.
      if (!this.hasAuthToken) {
        return;
      }

      this.latestTokenGenerated$.next(token);
    });
  }

  /**
   * Half the session lifetime, so every refresh finds a token with time left on it. A lifetime
   * below the shortest one the form accepts counts as that minimum, so a value set outside the
   * UI cannot turn renewal into a stream of calls, and a long one is still renewed on the
   * ceiling above rather than on a deadline middleware may never have granted.
   */
  private getTokenRenewalInterval(lifetime: number): number {
    const halfLifetime = Math.max(lifetime, minSessionLifetime) / 2;
    return Math.min(halfLifetime, this.maxRenewalIntervalSeconds) * 1000;
  }

  ngOnDestroy(): void {
    // Reset session state
    this.sessionInitialized = false;
    this.pendingAuthData = null;
  }
}
